import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, SkipSelf } from '@angular/core';
import { ControlContainer } from '@angular/forms';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { FieldType } from '../../enums/FieldType';
import { CompositeChildFieldVm, CompositeRowVm, CustomFieldTabContentVm, CustomFieldVm } from '../../models/CustomFieldContent';
import { CustomFieldService } from '../../services/custom-field.service';
import { PicklistItem } from '../../../user-defined-fields/models/Picklist';

@Component({
  selector: 'app-custom-field-content-form',
  templateUrl: './custom-field-content-form.component.html',
  styles: [`
    :host ::ng-deep .invalid-composite-cell input:not([type='checkbox']):not([type='radio']),
    :host ::ng-deep .invalid-composite-cell select,
    :host ::ng-deep .invalid-composite-cell textarea {
      border-left: 2px solid red;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [{
    provide: ControlContainer,
    deps: [[new SkipSelf(), ControlContainer]],
    useFactory: (container: ControlContainer) => container
  }]
})
export class CustomFieldContentFormComponent {
  @Input() vm: CustomFieldTabContentVm | null = null;
  @Input() usePublicLabels = false;
  @Input() publicApplicationForm = false;
  @Input() showLogButtons = false;
  @Input() showDateEffectiveBadges = false;
  @Input() fieldLabelColumnClass = 'col-4';
  @Input() fieldControlColumnClass = 'col-8';
  @Input() rowClass = 'row d-flex flex-wrap mb-3 align-items-center';
  @Input() namePrefix = 'fieldValue';

  @Output() valueChanged = new EventEmitter<void>();
  @Output() viewLogClicked = new EventEmitter<CustomFieldVm>();

  public FieldType = FieldType;
  public customFieldDateMinDate: NgbDateStruct = { year: 1950, month: 1, day: 1 };

  constructor(
    private customFieldService: CustomFieldService,
    private controlContainer: ControlContainer,
    private changeDetector: ChangeDetectorRef
  ) { }

  public getFieldValidationPattern(field: CustomFieldVm | CompositeChildFieldVm) {
    return this.customFieldService.getContentFieldValidationPattern(field);
  }

  public isBelgianNationalityNumberField(field: CustomFieldVm | CompositeChildFieldVm): boolean {
    return this.customFieldService.isBelgianNationalityNumberField(field);
  }

  public checkTime($event: KeyboardEvent, startElement = false) {
    const input = $event.target as HTMLInputElement;
    const key = $event.key;
    const allowedKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab', 'Backspace', 'Delete'];

    if (allowedKeys.includes(key)) {
      return;
    }

    if (!/^\d$/.test(key)) {
      $event.preventDefault();
      return;
    }

    setTimeout(() => {
      if (input.value.length === 2) {
        if (input.ariaLabel === 'Hours') {
          const minutesInput = input.parentElement?.parentElement?.querySelector<HTMLInputElement>('input[aria-label="Minutes"]');
          minutesInput?.focus();
          minutesInput?.select();
        } else if (input.ariaLabel === 'Minutes' && startElement) {
          const endHoursInput = document.querySelector<HTMLInputElement>('#faceToFaceEndTime input[aria-label="Hours"]');
          endHoursInput?.focus();
          endHoursInput?.select();
        }
      }

      this.changeDetector.markForCheck();
    });
  }

  public selectInput(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    input.select();
    this.markChanged();
  }

  public preventDecimalInput(event: KeyboardEvent): void {
    if (event.key === '.' || event.key === ',' || event.key === 'e' || event.key === '-' || event.key === '+') {
      event.preventDefault();
    }
  }

  public isMultipleChoiceItemChecked(value: any, multipleChoiceItem: PicklistItem) {
    return Array.isArray(value) && value.some((candidate: number) => candidate == multipleChoiceItem.picklistItemId);
  }

  public clickScalarMultipleChoiceItem(field: CustomFieldVm, multipleChoiceItem: PicklistItem) {
    if (!field.scalarValue) {
      return;
    }

    field.scalarValue.value = this.toggleMultipleChoiceSelection(field.scalarValue.value, multipleChoiceItem.picklistItemId);
    this.markChanged();
  }

  public clickCompositeMultipleChoiceItem(row: CompositeRowVm, childField: CompositeChildFieldVm, multipleChoiceItem: PicklistItem) {
    const cell = this.customFieldService.getCompositeCell(row, childField.fieldId);
    cell.value = this.toggleMultipleChoiceSelection(cell.value, multipleChoiceItem.picklistItemId);
    this.markChanged();
  }

  public getCompositeCell(row: CompositeRowVm, fieldId: number) {
    return this.customFieldService.getCompositeCell(row, fieldId);
  }

  public addCompositeValue(field: CustomFieldVm) {
    field.composite?.rows.push(this.customFieldService.createCompositeRow(field));
    this.markChanged();
  }

  public deleteCompositeValue(field: CustomFieldVm, row: CompositeRowVm) {
    if (!field.composite) {
      return;
    }

    if (row.rowId) {
      row.deleted = true;
    } else {
      field.composite.rows = field.composite.rows.filter(candidate => candidate !== row);
    }

    this.markChanged();
  }

  public getVisibleCompositeRows(field: CustomFieldVm): CompositeRowVm[] {
    return (field.composite?.rows ?? []).filter(row => !row.deleted);
  }

  public isCompositeCellMissing(field: CustomFieldVm, row: CompositeRowVm, childField: CompositeChildFieldVm): boolean {
    if (!this.publicApplicationForm || !childField.canEdit || row.deleted) {
      return false;
    }

    if (field.composite?.childFields.some(child => child.required)) {
      return childField.required && this.customFieldService.normalizeComparableValue(
        childField.fieldType, this.getCompositeCell(row, childField.fieldId).value) === null;
    }

    return this.customFieldService.isCompositeRowEmpty(field, row);
  }

  public canAddItem(field: CustomFieldVm) {
    const visibleRows = this.getVisibleCompositeRows(field).length;
    return !field.maximumAmount || field.maximumAmount > visibleRows;
  }

  public canDeleteItem(field: CustomFieldVm) {
    const visibleRows = this.getVisibleCompositeRows(field).length;
    return this.publicApplicationForm || !field.minimumAmount || field.minimumAmount < visibleRows;
  }

  public canChangeAddOrDeleteItem(field: CustomFieldVm | CompositeChildFieldVm, selectedValues: number[] | undefined, multipleChoiceItem: PicklistItem): boolean {
    const selectedCount = selectedValues?.length ?? 0;
    const isChecked = selectedValues?.some(candidate => candidate === multipleChoiceItem.picklistItemId);

    if (isChecked) {
      return this.publicApplicationForm || !field.minimumAmount || selectedCount > field.minimumAmount;
    }

    return !field.maximumAmount || selectedCount < field.maximumAmount;
  }

  public isFutureDateEffective(dateEffective: Date | string | null | undefined): boolean {
    if (!dateEffective) {
      return false;
    }

    const date = new Date(dateEffective);
    const today = new Date();

    date.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return date > today;
  }

  public trackCategories(index: number, item: any) {
    return item.categoryId;
  }

  public trackFields(index: number, item: CustomFieldVm) {
    return item.fieldId;
  }

  public trackChildFields(index: number, item: CompositeChildFieldVm) {
    return item.fieldId;
  }

  public trackCompositeRows(index: number, item: CompositeRowVm) {
    return item.rowId ?? item.clientRowId;
  }

  public getInputId(categoryIndex: number, fieldIndex: number, rowIndex?: number, childIndex?: number, itemId?: number) {
    return [
      this.namePrefix,
      categoryIndex,
      fieldIndex,
      rowIndex,
      childIndex,
      itemId
    ].filter(value => value !== undefined && value !== null).join('_');
  }

  private toggleMultipleChoiceSelection(currentValue: number[] | undefined, itemId: number): number[] {
    const selected = currentValue ?? [];
    return selected.some(candidate => candidate === itemId)
      ? selected.filter(candidate => candidate !== itemId)
      : [...selected, itemId];
  }

  public markChanged() {
    this.controlContainer.control?.markAsDirty();
    this.valueChanged.emit();
    this.changeDetector.markForCheck();
  }
}
